// [S-100] Platform Atlas — admin-only build/architecture explorer.
//
// Renders against the static JSON manifest produced by S-094's
// `scripts/build-platform-atlas.mjs` (`pnpm atlas:build`). Re-build the
// manifest pre-deploy to refresh the snapshot.
//
// THREE tabs: Executive summary, Overview, Roadmap. It had seven; four were
// removed because they had gone stale in the way only hand-kept lists do.
// "Screens & flows" was a hand-maintained mirror of App.tsx that had drifted
// badly (no /marketplace, no /drafts, still listing the retired /agents).
// "Apps & packages", "Modules & ports", and "Adapters" were flat dumps of
// 8 / 74 / 66 rows where every single row carried a green "shipped" pill — a
// status that, applied to everything, says nothing.
//
// The Executive summary deck STAYS. It was briefly removed on the assumption
// that a static asset must have drifted; that assumption was wrong and the
// slides are current (Identity · Why a vertical · Architecture · What it does
// today · The moat). It is the one narrative surface here — the other two tabs
// are inventories — so it leads.
//
// What survives is what stays true on its own: the Overview reads each service's
// role from its package.json `description`, the architecture rules from
// eslint.config.mjs, and the Roadmap from the VelocityPlan epic index — the
// tracker that is actually maintained.
//
// Audience: the route gate is `tenant:manage` (App.tsx), so a TENANT ADMIN
// reaches this screen — and gets the Executive summary deck, nothing else.
// Overview and Roadmap are internal engineering inventories (ports,
// adapters, lint rules, unshipped stories) and stay behind platform reach,
// as does the build stamp: a customer admin has no use for our git sha.
// tenant_user holds neither capability and sees no sidebar entry at all.
//
// The manifest is bundled into the SPA either way, so this split is a UX
// and surface-area decision, not a security boundary — it holds nothing
// tenant-sensitive.
import { useState } from "react";
import {
  PageHead,
  SectionHead,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components";
import manifest from "../data/platform-atlas.generated.json";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";

type Pkg = {
  dir: string;
  name: string;
  description: string;
  version: string;
  scripts: string[];
  dependencies: string[];
};

type EpicState =
  | "shipped"
  | "partial"
  | "active"
  | "reopened"
  | "designed"
  | "planned"
  | "blocked"
  | "shelved";

type Manifest = {
  readonly generatedAt: string;
  readonly git: { sha: string; branch: string };
  readonly apps: Pkg[];
  readonly packages: Pkg[];
  readonly ports: string[];
  readonly adapters: Array<{ module: string; name: string }>;
  readonly rules: Array<{ id: string; title: string; detail: string }>;
  readonly epics: Array<{
    id: string;
    name: string;
    stories: string;
    state: EpicState;
    summary: string;
  }>;
  readonly phases: Array<{
    ord: number;
    title: string;
    stories: Array<{
      id: string;
      title: string;
      // Older manifests (pre-status build) won't have this field; treat
      // missing as "pending" rather than crashing the screen.
      status?: StoryStatus;
      // Overlay-sourced text: `gap` for partial stories explains what
      // remains; `evidence` for shipped stories cites the file path.
      gap?: string;
      evidence?: string;
    }>;
  }>;
};

// `retired` — the story shipped and was then deliberately removed (e.g. S-214,
// the template clause library). Distinct from `pending`: nobody owes this work.
type StoryStatus =
  | "shipped"
  | "in_progress"
  | "pending"
  | "partial"
  | "retired";

const M = manifest as unknown as Manifest;

export function PlatformAtlas(): JSX.Element {
  const [tab, setTab] = useState("summary");
  // Platform reach → the inventories and the build stamp. A tenant admin
  // gets the narrative deck on its own, with no single-tab strip above it.
  const session = useSession();
  const internal = sessionCan(session, "platform:manage_tenants", {
    layer: "L1",
  });
  return (
    <>
      <PageHead
        eyebrow="Platform · platform atlas"
        title={
          <>
            What we built, <em>where it lives.</em>
          </>
        }
        sub={
          internal
            ? "Live view of the ContractStudio AI platform — sourced from the codebase, the implementation plan, and the Velocity Plan. Read-only. Refresh with pnpm atlas:build."
            : "What the platform is and how it is built — the internal explainer, read-only."
        }
        cta={
          internal ? (
            <div className="atlas__stamp" data-testid="atlas-stamp">
              <span className="atlas__stamp__chip">
                <span className="dot" />
                platform_admin
              </span>
              <span>
                <b>Updated</b> {formatRelative(M.generatedAt)}
              </span>
              <span>
                <b>git</b> {M.git.sha || "—"} · {M.git.branch || "—"}
              </span>
              <span>
                <b>generated</b> {formatTimestamp(M.generatedAt)}
              </span>
            </div>
          ) : undefined
        }
      />

      {!internal ? (
        <ExecSummaryTab />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList ariaLabel="Atlas sections" className="atlas__tabs">
            <TabsTrigger value="summary">Executive summary</TabsTrigger>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="roadmap">
              Roadmap
              <span className="ct">{M.epics.length}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary">
            <ExecSummaryTab />
          </TabsContent>
          <TabsContent value="overview">
            <OverviewTab />
          </TabsContent>
          <TabsContent value="roadmap">
            <RoadmapTab />
          </TabsContent>
        </Tabs>
      )}
    </>
  );
}

function StatusPill({ status }: { status: StoryStatus }): JSX.Element {
  if (status === "shipped") {
    return (
      <span className="s-pill s-pill--shipped" data-status="shipped">
        <span className="dot" />
        shipped
      </span>
    );
  }
  if (status === "in_progress") {
    return (
      <span className="s-pill s-pill--ip" data-status="in_progress">
        <span className="dot" />
        in progress
      </span>
    );
  }
  if (status === "partial") {
    return (
      <span className="s-pill s-pill--partial" data-status="partial">
        <span className="dot" />
        partial
      </span>
    );
  }
  if (status === "retired") {
    return (
      <span className="s-pill s-pill--retired" data-status="retired">
        <span className="dot" />
        retired
      </span>
    );
  }
  return (
    <span className="s-pill s-pill--plan" data-status="pending">
      <span className="dot" />
      pending
    </span>
  );
}

// Epic states reuse the story pill styles rather than inventing a second colour
// vocabulary: an epic in flight reads like a story in flight.
// Keyed by string, not EpicState, on purpose: the manifest is cast from JSON, so
// a state this build doesn't know about is a real runtime possibility. The index
// signature + noUncheckedIndexedAccess makes the fallback below type-required
// rather than dead code.
const EPIC_PILL_FALLBACK = { cls: "s-pill--plan", label: "planned" };
const EPIC_PILL: Record<string, { cls: string; label: string }> = {
  shipped: { cls: "s-pill--shipped", label: "shipped" },
  partial: { cls: "s-pill--partial", label: "partial" },
  active: { cls: "s-pill--ip", label: "in flight" },
  reopened: { cls: "s-pill--ip", label: "reopened" },
  designed: { cls: "s-pill--plan", label: "designed" },
  planned: { cls: "s-pill--plan", label: "planned" },
  blocked: { cls: "s-pill--partial", label: "blocked" },
  shelved: { cls: "s-pill--retired", label: "shelved" },
};

function EpicPill({ state }: { state: EpicState }): JSX.Element {
  const p = EPIC_PILL[state] ?? EPIC_PILL_FALLBACK;
  return (
    <span className={`s-pill ${p.cls}`} data-status={state}>
      <span className="dot" />
      {p.label}
    </span>
  );
}

// Executive summary — the imported Claude Design "Internal Explainer" deck,
// served as a static asset (apps/web/public/executive-summary.html) and
// embedded here. Kept out of the token-linted TSX surface on purpose: the
// deck carries its own palette, so it lives in a plain .html asset (ESLint
// targets .ts/.tsx only). Arrow keys / click-halves / the on-deck controls
// drive the five slides.
function ExecSummaryTab(): JSX.Element {
  return (
    <div className="atlas-exec">
      <p className="atlas-exec__note">
        Internal explainer · imported from Claude Design. Use ← / → or the
        on-slide controls to move through the five slides.
      </p>
      <iframe
        className="atlas-exec__frame"
        src="/executive-summary.html"
        title="ContractStudio AI — Internal Explainer"
        loading="lazy"
      />
    </div>
  );
}

function OverviewTab(): JSX.Element {
  return (
    <>
      <div className="atlas__sec">
        <SectionHead
          title={
            <>
              System map <em>· services · libraries · data</em>
            </>
          }
          action={
            <span className="atlas__sec__head__sub">
              roles read from each package.json · {M.ports.length} ports ·{" "}
              {M.adapters.length} adapters
            </span>
          }
        />
        <div className="atlas__map">
          <div className="atlas__col">
            <div className="atlas__col__lbl">Services · apps/*</div>
            {M.apps.map((a) => (
              <div className="atlas__node" key={a.dir}>
                <div className="atlas__node__row">
                  <span className="atlas__node__name">
                    {a.name} <code>{a.dir}</code>
                  </span>
                </div>
                <div className="atlas__node__sub">
                  {a.description || a.scripts.join(" · ")}
                </div>
              </div>
            ))}
          </div>
          <div className="atlas__col">
            <div className="atlas__col__lbl">Libraries · packages/*</div>
            {M.packages.map((p) => (
              <div className="atlas__node" key={p.dir}>
                <div className="atlas__node__row">
                  <span className="atlas__node__name">
                    {p.name} <code>{p.dir}</code>
                  </span>
                </div>
                <div className="atlas__node__sub">
                  {p.description || `v${p.version}`}
                </div>
              </div>
            ))}
          </div>
          <div className="atlas__col">
            <div className="atlas__col__lbl">Data &amp; retrieval</div>
            {DATA_NODES.map((n) => (
              <div className="atlas__node" key={n.name}>
                <div className="atlas__node__row">
                  <span className="atlas__node__name">{n.name}</span>
                </div>
                <div className="atlas__node__sub">{n.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="atlas__sec">
        <SectionHead
          title={
            <>
              Architecture rules <em>· enforced by ESLint</em>
            </>
          }
          action={
            <span className="atlas__sec__head__sub">
              eslint.config.mjs · {M.rules.length} rules · green lint = compliant
            </span>
          }
        />
        {M.rules.map((r) => (
          <div className="atlas__rule" key={r.id}>
            <div className="atlas__rule__head">
              <span className="atlas__rule__id">{r.id}</span>
              <span className="atlas__rule__title">{r.title}</span>
            </div>
            <div className="atlas__rule__body">{r.detail}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// The one hand-written list left on this screen, because infrastructure posture
// isn't derivable from package.json. Keep it honest: every claim below is
// checked against infra/terraform or the adapter factories, NOT aspirational.
// The previous version of this screen claimed a customer-managed key "per
// organization" and a Vertex vector index as the retrieval path — neither was
// true.
const DATA_NODES: Array<{ name: string; detail: string }> = [
  {
    name: "Firestore · primary store",
    detail:
      "Tenants, members, templates, structured contracts, chat + review sessions, marketplace listings. Per-tenant isolation enforced in the adapters. CMEK is OFF (firestore_cmek_enabled = false — needs a GCP allowlist).",
  },
  {
    name: "Vector search · Firestore-backed by default",
    detail:
      "CSAI_VECTOR_BACKEND unset → FirestoreVectorRepository (brute-force cosine, no idle cost). Vertex Matching Engine is selectable with =vertex, but the deployed index stays off in dev (~$48/mo idle). Both sit behind VectorStorePort.",
  },
  {
    name: "Embeddings · Vertex text-embedding-004",
    detail:
      "768-dim, pay-per-call, needs ADC locally. Retrieval keeps L1 (certified standard) and L2 (studio precedent) strictly separated — layer attribution survives every transformation.",
  },
  {
    name: "GCS · raw documents",
    detail:
      "Uploaded source files, encrypted with a project-level KMS key (module.storage.kms_key_id). Retention governed by tenant policy.",
  },
  {
    name: "Pub/Sub · ingestion topology",
    detail:
      "csai-ingestion-stage{1,2,3} + hybrid, Terraform-managed, with subscriber workers in apps/ingestion. Stage 2 is the only place outside the adapters allowed to touch an LLM SDK.",
  },
  {
    name: "Identity Platform · auth",
    detail:
      "Real tenant-scoped auth in dev and prod alike — no mock adapter outside tests. Authorization is capability-based (requireCapability), never a role-name check.",
  },
];

function storyStatus(s: { status?: StoryStatus }): StoryStatus {
  return s.status ?? "pending";
}

// Epics in the order a reader cares about: what's moving, then what's queued,
// then what's done. Within a group the VelocityPlan's own order is preserved.
const EPIC_GROUPS: Array<{ label: string; states: EpicState[] }> = [
  { label: "In flight", states: ["active", "reopened", "partial", "blocked"] },
  { label: "Planned", states: ["planned", "designed"] },
  { label: "Shipped", states: ["shipped", "shelved"] },
];

function RoadmapTab(): JSX.Element {
  const outstanding = M.phases
    .map((p) => ({
      ord: p.ord,
      title: p.title,
      stories: p.stories.filter((s) => {
        const st = storyStatus(s);
        return st !== "shipped" && st !== "retired";
      }),
    }))
    .filter((p) => p.stories.length > 0);
  const outstandingCt = outstanding.reduce((a, p) => a + p.stories.length, 0);
  const totalStories = M.phases.reduce((a, p) => a + p.stories.length, 0);

  return (
    <>
      <div className="atlas__sec">
        <SectionHead
          title={
            <>
              Epics <em>· docs/CSAI_VelocityPlan.md</em>
            </>
          }
          action={
            <span className="atlas__sec__head__sub" data-testid="roadmap-totals">
              {M.epics.length} epics · {totalStories - outstandingCt} of{" "}
              {totalStories} stories shipped
            </span>
          }
        />
        {EPIC_GROUPS.map((group) => {
          const rows = M.epics.filter((e) => group.states.includes(e.state));
          if (rows.length === 0) return null;
          return (
            <div className="atlas__phase" key={group.label}>
              <div className="atlas__phase__head">
                <h3>
                  {group.label} <em>· {rows.length} epics</em>
                </h3>
              </div>
              {rows.map((e) => (
                <div className="atlas__node" key={e.id} data-status={e.state}>
                  <div className="atlas__node__row">
                    <span className="atlas__node__name">
                      {e.name} <code>{e.id}</code>
                    </span>
                    <EpicPill state={e.state} />
                  </div>
                  <div className="atlas__node__sub">
                    {e.stories.length > 0 ? `${e.stories} · ` : ""}
                    {e.summary}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="atlas__sec">
        <SectionHead
          title={
            <>
              Outstanding stories <em>· docs/CSAI_ImplementationPlan.md</em>
            </>
          }
          action={
            <span className="atlas__sec__head__sub">
              {outstandingCt} not yet shipped · shipped and retired stories are
              hidden
            </span>
          }
        />
        {outstanding.map((phase) => (
          <div className="atlas__phase" key={phase.ord}>
            <div className="atlas__phase__head">
              <h3>
                Phase {phase.ord} <em>· {phase.title}</em>
              </h3>
              <span className="atlas__phase__progress">
                <span>{phase.stories.length} outstanding</span>
              </span>
            </div>
            {phase.stories.map((s) => {
              const status = storyStatus(s);
              return (
                <div className="atlas__story" key={s.id} data-status={status}>
                  <span className="atlas__story__id">{s.id}</span>
                  <span className="atlas__story__title">{s.title}</span>
                  <StatusPill status={status} />
                  {typeof s.gap === "string" && (
                    <p className="atlas__story__gap">{s.gap}</p>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

function formatTimestamp(iso: string): string {
  if (iso.length === 0) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hour12: false,
    timeZoneName: "short",
  });
}

function formatRelative(iso: string): string {
  if (iso.length === 0) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  return `${mo}mo ago`;
}
